import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

import type { ChatAgent } from '@/features/global-ai-chat/api/types';

/** パネル幅のデフォルト値（旧 max-w-sm = 384px 相当） */
export const AI_CHAT_PANEL_DEFAULT_WIDTH = 384;
/** パネル幅の最小値 */
export const AI_CHAT_PANEL_MIN_WIDTH = 320;
/** パネル幅の最大値（ビューポート比の上限は利用側で別途 clamp する） */
export const AI_CHAT_PANEL_MAX_WIDTH = 720;

type GlobalAiChatStore = {
  isOpen: boolean;
  activeSessionId: string | null;
  selectedModelId: ChatAgent | null;
  dismissedContextIds: string[];
  panelWidth: number;
  setOpen: (isOpen: boolean) => void;
  toggleOpen: () => void;
  setActiveSessionId: (sessionId: string | null) => void;
  setSelectedModelId: (modelId: ChatAgent) => void;
  dismissContextId: (contextId: string) => void;
  clearDismissedContextIds: () => void;
  setPanelWidth: (panelWidth: number) => void;
};

export const useGlobalAiChatStore = create<GlobalAiChatStore>()(
  devtools(
    persist(
      (set) => ({
        isOpen: false,
        activeSessionId: null,
        selectedModelId: null,
        dismissedContextIds: [],
        panelWidth: AI_CHAT_PANEL_DEFAULT_WIDTH,
        setOpen: (isOpen) => set({ isOpen }, false, 'globalAiChat/setOpen'),
        toggleOpen: () =>
          set(
            (state) => ({ isOpen: !state.isOpen }),
            false,
            'globalAiChat/toggleOpen',
          ),
        setActiveSessionId: (activeSessionId) =>
          set({ activeSessionId }, false, 'globalAiChat/setActiveSessionId'),
        setSelectedModelId: (selectedModelId) =>
          set({ selectedModelId }, false, 'globalAiChat/setSelectedModelId'),
        dismissContextId: (contextId) =>
          set(
            (state) =>
              state.dismissedContextIds.includes(contextId)
                ? state
                : {
                    dismissedContextIds: [
                      ...state.dismissedContextIds,
                      contextId,
                    ],
                  },
            false,
            'globalAiChat/dismissContextId',
          ),
        clearDismissedContextIds: () =>
          set(
            { dismissedContextIds: [] },
            false,
            'globalAiChat/clearDismissedContextIds',
          ),
        setPanelWidth: (panelWidth) =>
          set({ panelWidth }, false, 'globalAiChat/setPanelWidth'),
      }),
      {
        name: 'global-ai-chat-panel',
        // パネル幅のみ localStorage に永続化する
        partialize: (state) => ({ panelWidth: state.panelWidth }),
      },
    ),
    { name: 'GlobalAiChatStore' },
  ),
);
